import { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Move, Type, Save, X, Upload, Loader2, Frame, Image } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { SpineTextField } from './CoverFullPreview';
import { getFieldPositionStyles } from './coverFieldStyles';
import { CoverFrontPanel } from './CoverFrontPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SAMPLE_VALUES = {
  // Use full "first + last" name (similar to real production data like
  // "MANOLO MONROY") so admins design templates that don't overflow
  // when the actual protagonist has a long combined name.
  protagonist_name: 'MANOLO MONROY',
  subtitle: 'Un libro para',
  title: 'Las Aventuras de un Hombre Extraordinario',
  branding: 'A GIFT OF MEMORIES',
};

const SAMPLE_PHOTO = 'https://images.unsplash.com/photo-1664101606938-e664f5852fac?w=400&h=550&fit=crop&crop=face';

const FONT_OPTIONS = [
  { value: 'sans-serif', label: 'Sans Serif' },
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: 'Times New Roman, serif', label: 'Times New Roman' },
  { value: 'Courier New, monospace', label: 'Courier New' },
];

export const CoverTemplateEditor = ({ template, open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [fields, setFields] = useState(template?.fields_config || {});
  const [activeField, setActiveField] = useState('title');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [name, setName] = useState(template?.name || '');
  const [bgType, setBgType] = useState(template?.background_type || 'photo');
  const [photoFrame, setPhotoFrame] = useState(template?.photo_frame || { type: 'full' });
  const [editingFrame, setEditingFrame] = useState(false);
  const [activeTab, setActiveTab] = useState('front');
  const [sampleTexts, setSampleTexts] = useState({
    ...SAMPLE_VALUES,
    ...(template?.sample_texts || {}),
  });
  const [spineConfig, setSpineConfig] = useState(() => {
    const sc = template?.spine_config || {};
    return {
      background_color: sc.background_color || '#1a1a1a',
      fields: sc.fields || {
        protagonist_name: { x: 50, y: 5, fontSize: 9, color: '#ffffff', fontWeight: 'bold', fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', ...(sc.protagonist_name || {}) },
        title: { x: 50, y: 40, fontSize: 8, color: '#ffffff', fontWeight: 'bold', fontFamily: 'Georgia, serif', textTransform: 'none', letterSpacing: '0', textAlign: 'center', ...(sc.title || {}) },
        branding: { x: 50, y: 85, fontSize: 6, color: 'rgba(255,255,255,0.6)', fontWeight: '400', fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', ...(sc.branding || {}) },
      }
    };
  });
  const [backConfig, setBackConfig] = useState(() => {
    const bc = template?.back_config || {};
    return {
      background_color: bc.background_color || '#ffffff',
      num_reviews: bc.num_reviews || 4,
      show_badge: bc.show_badge !== false,
      review_color: bc.review_color || '#333333',
      reviewer_color: bc.reviewer_color || '#666666',
      fields: bc.fields || {
        heading: { x: 5, y: 3, fontSize: 14, color: bc.heading_color || '#1a1a1a', fontWeight: 'bold', fontFamily: 'sans-serif', textTransform: 'none', letterSpacing: '0', textAlign: 'left' },
        branding: { x: 5, y: 90, fontSize: 11, color: bc.branding_color || '#c97b3a', fontWeight: 'bold', fontFamily: 'sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'left' },
        website: { x: 5, y: 86, fontSize: 8, color: '#666666', fontWeight: '400', fontFamily: 'sans-serif', textTransform: 'none', letterSpacing: '0', textAlign: 'left' },
      },
      branding_text: bc.branding_text || 'A GIFT OF MEMORIES',
      website_text: bc.website_text || 'agiftofmemories.com',
    };
  });

  // Drag state
  const dragRef = useRef(null);
  const containerRef = useRef(null);

  const handleDragStart = useCallback((e, fieldKey, tab) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveField(fieldKey);
    const container = e.target.closest('[data-drag-container]');
    if (!container) return;
    const rect = container.getBoundingClientRect();
    dragRef.current = { fieldKey, tab, startX: e.clientX, startY: e.clientY, rect };
    
    const currentF = tab === 'spine' ? (spineConfig.fields?.[fieldKey] || {})
      : tab === 'back' ? (backConfig.fields?.[fieldKey] || {})
      : (fields[fieldKey] || {});
    dragRef.current.startFieldX = currentF.x ?? 0;
    dragRef.current.startFieldY = currentF.y ?? 0;

    const onMove = (ev) => {
      if (!dragRef.current) return;
      const d = dragRef.current;
      const dx = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dy = ((ev.clientY - d.startY) / d.rect.height) * 100;
      const newX = Math.max(0, Math.min(95, d.startFieldX + dx));
      const newY = Math.max(0, Math.min(95, d.startFieldY + dy));
      if (d.tab === 'spine') {
        setSpineConfig(prev => ({ ...prev, fields: { ...prev.fields, [d.fieldKey]: { ...(prev.fields?.[d.fieldKey] || {}), x: Math.round(newX), y: Math.round(newY) } } }));
      } else if (d.tab === 'back') {
        setBackConfig(prev => ({ ...prev, fields: { ...prev.fields, [d.fieldKey]: { ...(prev.fields?.[d.fieldKey] || {}), x: Math.round(newX), y: Math.round(newY) } } }));
      } else {
        setFields(prev => ({ ...prev, [d.fieldKey]: { ...prev[d.fieldKey], x: Math.round(newX), y: Math.round(newY) } }));
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [fields, spineConfig, backConfig]);

  const SPINE_LABELS = { protagonist_name: 'Nombre', title: 'Titulo', branding: 'Marca' };
  const SPINE_SAMPLES = { protagonist_name: 'MANOLO', title: 'Las Aventuras de un Hombre Extraordinario', branding: 'A GIFT OF MEMORIES' };
  const BACK_LABELS = { heading: 'Encabezado', branding: 'Marca', website: 'Web' };
  const BACK_SAMPLES = { heading: 'Elogios para Las Aventuras...', branding: 'A GIFT OF MEMORIES', website: 'agiftofmemories.com' };

  const FIELD_LABELS = {
    protagonist_name: t('coverEditor.fieldProtagonistName'),
    subtitle: t('coverEditor.fieldSubtitle'),
    title: t('coverEditor.fieldTitle'),
    branding: t('coverEditor.fieldBranding'),
  };

  // Unified field getter based on active tab
  const getActiveFields = () => {
    if (activeTab === 'spine') return spineConfig.fields || {};
    if (activeTab === 'back') return backConfig.fields || {};
    return fields;
  };
  const getActiveLabels = () => {
    if (activeTab === 'spine') return SPINE_LABELS;
    if (activeTab === 'back') return BACK_LABELS;
    return FIELD_LABELS;
  };
  const updateActiveField = (fieldKey, prop, value) => {
    if (activeTab === 'spine') {
      setSpineConfig(prev => ({ ...prev, fields: { ...prev.fields, [fieldKey]: { ...(prev.fields?.[fieldKey] || {}), [prop]: value } } }));
    } else if (activeTab === 'back') {
      setBackConfig(prev => ({ ...prev, fields: { ...prev.fields, [fieldKey]: { ...(prev.fields?.[fieldKey] || {}), [prop]: value } } }));
    } else {
      updateField(fieldKey, prop, value);
    }
  };
  const currentFields = getActiveFields();
  const currentLabels = getActiveLabels();
  const af = currentFields[activeField] || {};

  const previewUrl = template?.preview_image_path
    ? `${API_URL}/api/cover-templates/${template.template_id}/preview-image`
    : null;

  const updateField = (fieldKey, prop, value) => {
    setFields(prev => ({
      ...prev,
      [fieldKey]: { ...prev[fieldKey], [prop]: value },
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/cover-templates/${template.template_id}`, {
        name,
        fields_config: fields,
        background_type: bgType,
        photo_frame: photoFrame,
        spine_config: spineConfig,
        back_config: backConfig,
        sample_texts: sampleTexts,
      }, { withCredentials: true });
      toast.success(t('coverEditor.templateSaved'));
      onSaved?.();
      onClose();
    } catch {
      toast.error(t('coverEditor.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/admin/cover-templates/${template.template_id}/upload-preview`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('coverEditor.imageUpdated'));
      onSaved?.();
    } catch {
      toast.error(t('coverEditor.uploadError'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] p-0 flex flex-col overflow-hidden" data-testid="cover-editor-dialog">
        <DialogHeader className="px-5 py-3 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="font-heading text-lg">{t('coverEditor.editorTitle')}</DialogTitle>
            <div className="flex items-center gap-2">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-56 h-8 text-sm"
                placeholder={t('coverEditor.coverName')}
                data-testid="editor-name-input"
              />
              <Button size="sm" onClick={handleSave} disabled={saving} className="btn-primary gap-1.5" data-testid="editor-save-btn">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {t('common.save')}
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Preview area */}
          <div className="flex-1 flex flex-col bg-neutral-100 overflow-hidden">
            {/* Tabs */}
            <div className="flex items-center gap-1 px-4 pt-3 pb-2 bg-neutral-100 flex-shrink-0">
              {[
                { key: 'front', label: 'Portada' },
                { key: 'spine', label: 'Lomo' },
                { key: 'back', label: 'Contraportada' },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    const firstKey = tab.key === 'spine' ? 'protagonist_name' : tab.key === 'back' ? 'heading' : 'title';
                    setActiveField(firstKey);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${activeTab === tab.key ? 'bg-white shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-white/50'}`}
                  data-testid={`editor-tab-${tab.key}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Preview */}
            <div className="flex-1 flex items-center justify-center p-6 overflow-auto">
              {activeTab === 'front' && (
                <div
                  className="relative shadow-2xl rounded-md overflow-hidden"
                  style={{ width: 380, height: 540 }}
                  data-testid="editor-cover-preview"
                  data-drag-container="front"
                >
                  {/* Background + text rendered by the SAME component the
                      promoter sees (CoverFrontPanel). This guarantees the
                      editor and every preview surface stay pixel-identical:
                      change a template → both update simultaneously. */}
                  <CoverFrontPanel
                    template={{
                      template_id: template?.template_id,
                      background_type: bgType,
                      preview_image_path: template?.preview_image_path,
                      photo_frame: photoFrame,
                      fields_config: fields,
                    }}
                    fieldValues={sampleTexts}
                  />
                  {/* DRAG HANDLES — invisible boxes over each text field so
                      the admin can grab and move them. Visuals come from
                      CoverFrontPanel above; this layer is interaction-only. */}
                  {Object.entries(fields).map(([key, cfg]) => {
                    const pos = getFieldPositionStyles(cfg);
                    return (
                    <div
                      key={key}
                      className={`absolute cursor-grab active:cursor-grabbing select-none z-30 ${activeField === key ? 'ring-2 ring-blue-400 ring-offset-1' : 'hover:ring-1 hover:ring-white/40'}`}
                      style={{
                        ...pos,
                        // Match the rendered text box approximately so the
                        // grab area is clickable but not blocking visuals.
                        minWidth: '20%',
                        minHeight: `${(cfg.fontSize || 14) * 1.6}px`,
                        maxHeight: '40%',
                        background: 'rgba(0,0,0,0)',
                      }}
                      onMouseDown={(e) => handleDragStart(e, key, 'front')}
                      onClick={() => setActiveField(key)}
                      data-testid={`editor-field-${key}`}
                    />
                  );})}

              {/* Photo frame editing overlay (dashed border, only when editing) */}
              {photoFrame?.type === 'framed' && editingFrame && (() => {
                const fr = photoFrame;
                const left = (fr.x || 50) - (fr.width || 40) / 2;
                const top = (fr.y || 40) - (fr.height || 50) / 2;
                const borderRadius = fr.shape === 'circle' ? '50%' : fr.shape === 'oval' ? '50%' : '4px';
                return (
                  <div
                    className="absolute border-2 border-dashed border-cyan-400 z-30 pointer-events-none"
                    style={{
                      left: `${left}%`, top: `${top}%`,
                      width: `${fr.width || 40}%`, height: `${fr.height || 50}%`,
                      borderRadius,
                      transform: fr.rotation ? `rotate(${fr.rotation}deg)` : undefined,
                    }}
                    data-testid="editor-frame-overlay"
                  />
                );
              })()}
            </div>
          )}

              {activeTab === 'spine' && (
                <div className="shadow-2xl rounded-md overflow-hidden relative" style={{ width: 80, height: 540, background: spineConfig.background_color || '#1a1a1a' }} data-testid="editor-spine-preview" data-drag-container="spine">
                  {Object.entries(spineConfig.fields || {}).map(([key, cfg]) => {
                    const isActive = activeField === key && activeTab === 'spine';
                    return (
                      <SpineTextField
                        key={key}
                        cfg={cfg}
                        value={sampleTexts[`spine_${key}`] || SPINE_SAMPLES[key] || key}
                        baseFontSize={cfg.fontSize || 8}
                        isTitle={key === 'title'}
                        draggable
                        onMouseDown={(e) => handleDragStart(e, key, 'spine')}
                        extraClassName={`cursor-grab active:cursor-grabbing select-none transition-shadow ${isActive ? 'ring-2 ring-blue-400 ring-offset-1 z-20' : 'z-10 hover:ring-1 hover:ring-white/40'}`}
                      />
                    );
                  })}
                </div>
              )}

              {activeTab === 'back' && (
                <div className="shadow-2xl rounded-md overflow-hidden relative" style={{ width: 380, height: 540, background: backConfig.background_color || '#ffffff' }} data-testid="editor-back-preview" data-drag-container="back">
                  {/* Positioned text fields */}
                  {Object.entries(backConfig.fields || {}).map(([key, cfg]) => {
                    const pos = getFieldPositionStyles({ ...cfg, x: cfg.x ?? 5 });
                    return (
                    <div
                      key={key}
                      className={`absolute cursor-grab active:cursor-grabbing select-none transition-shadow ${activeField === key && activeTab === 'back' ? 'ring-2 ring-blue-400 ring-offset-1 z-20' : 'z-10 hover:ring-1 hover:ring-gray-300'}`}
                      style={{
                        ...pos,
                        fontSize: cfg.fontSize || 12,
                        color: cfg.color || '#333',
                        fontWeight: cfg.fontWeight || 'normal',
                        fontFamily: cfg.fontFamily || 'sans-serif',
                        textTransform: cfg.textTransform || 'none',
                        letterSpacing: cfg.letterSpacing || 'normal',
                        lineHeight: 1.3,
                        padding: '2px 4px',
                      }}
                      onMouseDown={(e) => handleDragStart(e, key, 'back')}
                    >
                      {sampleTexts[`back_${key}`] || BACK_SAMPLES[key] || key}
                    </div>
                  );})}
                  {/* Sample reviews (non-interactive, positioned between heading and branding) */}
                  <div className="absolute px-6" style={{ top: '15%', left: 0, right: 0, bottom: '18%', overflow: 'hidden' }}>
                    {Array.from({ length: backConfig.num_reviews || 4 }).map((_, i) => (
                      <div key={i} className="mb-2" style={{ fontSize: 10 }}>
                        <p style={{ color: backConfig.review_color || '#333', lineHeight: 1.4 }}>"Resena de ejemplo {i + 1}: Un texto brillante..."</p>
                        <p className="font-bold mt-0.5" style={{ color: backConfig.reviewer_color || '#666', fontSize: 9 }}>Revista Ficticia {i + 1}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right: Controls Panel */}
          <div className="w-80 border-l bg-white flex flex-col overflow-hidden flex-shrink-0">
            {activeTab === 'front' && (<>
            {/* Background type */}
            <div className="px-4 py-3 border-b bg-muted/20 flex-shrink-0">
              <Label className="text-xs font-medium mb-2 block">Tipo de fondo</Label>
              <Select value={bgType} onValueChange={setBgType}>
                <SelectTrigger className="h-8 text-xs" data-testid="editor-bg-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="photo" className="text-xs">{t('coverEditor.protagonistPhoto')}</SelectItem>
                  <SelectItem value="custom" className="text-xs">{t('coverEditor.templateImage')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Photo frame controls */}
            {bgType === 'custom' && (
              <div className="px-4 py-3 border-b bg-cyan-50/40 flex-shrink-0 space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-medium flex items-center gap-1.5">
                    <Frame className="w-3.5 h-3.5 text-cyan-600" /> {t('coverEditor.photoFrame')}
                  </Label>
                  <button
                    type="button"
                    onClick={() => setEditingFrame(!editingFrame)}
                    className={`text-[10px] px-2 py-0.5 rounded font-medium transition-colors ${editingFrame ? 'bg-cyan-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
                    data-testid="editor-toggle-frame"
                  >
                    {editingFrame ? t('coverEditor.hide') : t('common.edit')}
                  </button>
                </div>
                <Select
                  value={photoFrame?.type || 'full'}
                  onValueChange={(v) => setPhotoFrame(prev => ({
                    ...prev,
                    type: v,
                    ...(v === 'framed' && !prev.shape ? { shape: 'oval', x: 50, y: 35, width: 45, height: 45, rotation: 0 } : {})
                  }))}
                >
                  <SelectTrigger className="h-7 text-xs" data-testid="editor-frame-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full" className="text-xs">{t('coverEditor.fullPhoto')}</SelectItem>
                    <SelectItem value="framed" className="text-xs">{t('coverEditor.framedPhoto')}</SelectItem>
                  </SelectContent>
                </Select>
                {photoFrame?.type === 'framed' && editingFrame && (
                  <div className="space-y-2.5">
                    <Select
                      value={photoFrame.shape || 'oval'}
                      onValueChange={(v) => setPhotoFrame(prev => ({ ...prev, shape: v }))}
                    >
                      <SelectTrigger className="h-7 text-xs" data-testid="editor-frame-shape">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="oval" className="text-xs">{t('coverEditor.oval')}</SelectItem>
                        <SelectItem value="circle" className="text-xs">{t('coverEditor.circle')}</SelectItem>
                        <SelectItem value="rectangle" className="text-xs">{t('coverEditor.rectangle')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-muted-foreground">{t('coverEditor.horizontalPos')}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{photoFrame.x ?? 50}%</span>
                      </div>
                      <Slider value={[photoFrame.x ?? 50]} min={5} max={95} step={1} onValueChange={([v]) => setPhotoFrame(prev => ({ ...prev, x: v }))} />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-muted-foreground">{t('coverEditor.verticalPos')}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{photoFrame.y ?? 35}%</span>
                      </div>
                      <Slider value={[photoFrame.y ?? 35]} min={5} max={95} step={1} onValueChange={([v]) => setPhotoFrame(prev => ({ ...prev, y: v }))} />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-muted-foreground">{t('coverEditor.width')}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{photoFrame.width ?? 45}%</span>
                      </div>
                      <Slider value={[photoFrame.width ?? 45]} min={10} max={95} step={1} onValueChange={([v]) => setPhotoFrame(prev => ({ ...prev, width: v }))} />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-muted-foreground">{t('coverEditor.height')}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{photoFrame.height ?? 45}%</span>
                      </div>
                      <Slider value={[photoFrame.height ?? 45]} min={10} max={95} step={1} onValueChange={([v]) => setPhotoFrame(prev => ({ ...prev, height: v }))} />
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-[10px] text-muted-foreground">{t('coverEditor.rotation')}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">{photoFrame.rotation ?? 0}°</span>
                      </div>
                      <Slider value={[photoFrame.rotation ?? 0]} min={-45} max={45} step={1} onValueChange={([v]) => setPhotoFrame(prev => ({ ...prev, rotation: v }))} />
                    </div>

                    {/* Background for framed mode */}
                    <div className="pt-2 mt-2 border-t border-cyan-200/60 space-y-2">
                      <span className="text-[10px] font-medium text-foreground">{t('coverEditor.coverBackground')}</span>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={photoFrame.bg_color || '#1a1a1a'}
                          onChange={(e) => setPhotoFrame(prev => ({ ...prev, bg_color: e.target.value }))}
                          className="w-7 h-7 rounded border cursor-pointer flex-shrink-0"
                          data-testid="editor-frame-bg-color"
                        />
                        <Input
                          value={photoFrame.bg_color || '#1a1a1a'}
                          onChange={(e) => setPhotoFrame(prev => ({ ...prev, bg_color: e.target.value }))}
                          className="h-7 text-[10px] font-mono flex-1"
                          placeholder="#1a1a1a"
                        />
                      </div>
                      <label className="flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] font-medium rounded border border-dashed border-cyan-300 bg-white hover:bg-cyan-50/50 cursor-pointer transition-colors">
                        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Image className="w-3 h-3" />}
                        {uploading ? t('coverEditor.uploading') : previewUrl ? t('coverEditor.changeBgImage') : t('coverEditor.uploadBgImage')}
                        <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleImageUpload} data-testid="editor-frame-bg-upload" />
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* Field tabs */}
            <div className="flex border-b overflow-x-auto flex-shrink-0">
              {Object.keys(fields).map(key => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveField(key)}
                  className={`px-3 py-2.5 text-[11px] font-medium whitespace-nowrap transition-colors border-b-2 ${activeField === key ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                  data-testid={`editor-tab-${key}`}
                >
                  {FIELD_LABELS[key]?.split(' ')[0] || key}
                </button>
              ))}
            </div>

            {/* Controls for active field */}
            <div className="flex-1 overflow-y-auto p-4 space-y-5">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <Move className="w-4 h-4 text-primary" />
                {FIELD_LABELS[activeField]}
              </div>

              {/* Editable text content */}
              <div className="space-y-1">
                <Label className="text-xs">Texto</Label>
                <Input
                  value={sampleTexts[activeField] || ''}
                  onChange={(e) => setSampleTexts(prev => ({ ...prev, [activeField]: e.target.value }))}
                  className="h-8 text-sm"
                  placeholder={SAMPLE_VALUES[activeField] || ''}
                  data-testid={`editor-text-${activeField}`}
                />
              </div>

              {/* Position X */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">{t('coverEditor.posHorizontal')}</Label>
                  <span className="text-xs text-muted-foreground font-mono">{af.x ?? 0}%</span>
                </div>
                <Slider
                  value={[af.x ?? 0]}
                  min={0} max={95} step={1}
                  onValueChange={([v]) => updateField(activeField, 'x', v)}
                  data-testid="editor-slider-x"
                />
              </div>

              {/* Position Y */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs">{t('coverEditor.posVertical')}</Label>
                  <span className="text-xs text-muted-foreground font-mono">{af.y ?? 0}%</span>
                </div>
                <Slider
                  value={[af.y ?? 0]}
                  min={0} max={95} step={1}
                  onValueChange={([v]) => updateField(activeField, 'y', v)}
                  data-testid="editor-slider-y"
                />
              </div>

              {/* Font size */}
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label className="text-xs flex items-center gap-1"><Type className="w-3 h-3" /> {t('coverEditor.fontSize')}</Label>
                  <span className="text-xs text-muted-foreground font-mono">{af.fontSize ?? 14}px</span>
                </div>
                <Slider
                  value={[af.fontSize ?? 14]}
                  min={6} max={48} step={1}
                  onValueChange={([v]) => updateField(activeField, 'fontSize', v)}
                  data-testid="editor-slider-fontsize"
                />
              </div>

              {/* Color */}
              <div className="space-y-2">
                <Label className="text-xs">{t('coverEditor.color')}</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={af.color?.startsWith('#') ? af.color.slice(0, 7) : '#ffffff'}
                    onChange={(e) => updateField(activeField, 'color', e.target.value)}
                    className="w-8 h-8 rounded border cursor-pointer"
                    data-testid="editor-color-picker"
                  />
                  <Input
                    value={af.color || '#ffffff'}
                    onChange={(e) => updateField(activeField, 'color', e.target.value)}
                    className="h-8 text-xs font-mono flex-1"
                    data-testid="editor-color-input"
                  />
                </div>
              </div>

              {/* Font family */}
              <div className="space-y-2">
                <Label className="text-xs">{t('coverEditor.typography')}</Label>
                <Select value={af.fontFamily || 'sans-serif'} onValueChange={(v) => updateField(activeField, 'fontFamily', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="editor-font-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(f => (
                      <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Font weight */}
              <div className="space-y-2">
                <Label className="text-xs">{t('coverEditor.weight')}</Label>
                <Select value={af.fontWeight || '400'} onValueChange={(v) => updateField(activeField, 'fontWeight', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="editor-weight-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="300" className="text-xs">Light (300)</SelectItem>
                    <SelectItem value="400" className="text-xs">Normal (400)</SelectItem>
                    <SelectItem value="600" className="text-xs">Semibold (600)</SelectItem>
                    <SelectItem value="bold" className="text-xs">Bold</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Text transform */}
              <div className="space-y-2">
                <Label className="text-xs">{t('coverEditor.transform')}</Label>
                <Select value={af.textTransform || 'none'} onValueChange={(v) => updateField(activeField, 'textTransform', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="editor-transform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">{t('common.normal')}</SelectItem>
                    <SelectItem value="uppercase" className="text-xs">{t('coverEditor.uppercase')}</SelectItem>
                    <SelectItem value="capitalize" className="text-xs">{t('coverEditor.capitalize')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Text align */}
              <div className="space-y-2">
                <Label className="text-xs">{t('coverEditor.alignment')}</Label>
                <Select value={af.textAlign || 'left'} onValueChange={(v) => updateField(activeField, 'textAlign', v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="editor-align-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="left" className="text-xs">{t('coverEditor.alignLeft')}</SelectItem>
                    <SelectItem value="center" className="text-xs">{t('coverEditor.alignCenter')}</SelectItem>
                    <SelectItem value="right" className="text-xs">{t('coverEditor.alignRight')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Image upload */}
            <div className="border-t p-3 flex-shrink-0 space-y-2">
              {photoFrame?.type === 'framed' && (
                <p className="text-[10px] text-muted-foreground text-center px-2">
                  La foto del protagonista se colocara automaticamente en el marco al seleccionar esta plantilla en un libro.
                </p>
              )}
              <label className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-md border border-dashed border-input bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {uploading ? t('coverEditor.uploading') : previewUrl
                  ? (photoFrame?.type === 'framed' ? t('coverEditor.changeBgDesign') : t('coverEditor.changeBgImage'))
                  : t('coverEditor.uploadCover')}
                <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handleImageUpload} data-testid="editor-upload-input" />
              </label>
            </div>
            </>)}

            {/* SPINE/BACK: Unified controls - same as front */}
            {(activeTab === 'spine' || activeTab === 'back') && (
              <>
              {/* Background color */}
              <div className="px-4 py-3 border-b bg-muted/20 flex-shrink-0">
                <Label className="text-xs font-medium mb-2 block">Color de fondo</Label>
                <div className="flex items-center gap-2">
                  <input type="color" value={(activeTab === 'spine' ? spineConfig.background_color : backConfig.background_color) || '#1a1a1a'} onChange={(e) => { activeTab === 'spine' ? setSpineConfig(prev => ({ ...prev, background_color: e.target.value })) : setBackConfig(prev => ({ ...prev, background_color: e.target.value })); }} className="w-8 h-8 rounded border cursor-pointer" />
                  <Input value={(activeTab === 'spine' ? spineConfig.background_color : backConfig.background_color) || ''} onChange={(e) => { activeTab === 'spine' ? setSpineConfig(prev => ({ ...prev, background_color: e.target.value })) : setBackConfig(prev => ({ ...prev, background_color: e.target.value })); }} className="h-8 text-xs font-mono flex-1" />
                </div>
              </div>

              {/* Field tabs */}
              <div className="px-4 pt-3 pb-1 flex gap-1 flex-wrap flex-shrink-0 border-b bg-muted/10">
                {Object.keys(currentFields).map(key => (
                  <button key={key} onClick={() => setActiveField(key)} className={`px-2 py-1 text-[10px] rounded-md font-medium transition-colors ${activeField === key ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-muted-foreground hover:bg-muted'}`}>
                    {currentLabels[key] || key}
                  </button>
                ))}
              </div>

              {/* Active field label */}
              <div className="px-4 pt-3 pb-1 flex-shrink-0">
                <div className="flex items-center gap-1.5 mb-2">
                  <Move className="w-3.5 h-3.5 text-muted-foreground" />
                  <Label className="text-xs font-semibold">{currentLabels[activeField] || activeField}</Label>
                </div>
              </div>

              {/* Controls */}
              <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-4">
                {/* Editable text content */}
                <div className="space-y-1">
                  <Label className="text-xs">Texto</Label>
                  <Input
                    value={sampleTexts[activeTab === 'spine' ? `spine_${activeField}` : `back_${activeField}`] || ''}
                    onChange={(e) => {
                      const key = activeTab === 'spine' ? `spine_${activeField}` : `back_${activeField}`;
                      setSampleTexts(prev => ({ ...prev, [key]: e.target.value }));
                    }}
                    className="h-8 text-sm"
                    placeholder={activeTab === 'spine' ? (SPINE_SAMPLES[activeField] || '') : (BACK_SAMPLES[activeField] || '')}
                    data-testid={`editor-text-${activeTab}-${activeField}`}
                  />
                </div>

                {/* Position X */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">{t('coverEditor.posHorizontal')}</Label>
                    <span className="text-xs text-muted-foreground font-mono">{af.x ?? 0}%</span>
                  </div>
                  <Slider value={[af.x ?? 0]} min={0} max={95} step={1} onValueChange={([v]) => updateActiveField(activeField, 'x', v)} />
                </div>

                {/* Position Y */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs">{t('coverEditor.posVertical')}</Label>
                    <span className="text-xs text-muted-foreground font-mono">{af.y ?? 0}%</span>
                  </div>
                  <Slider value={[af.y ?? 0]} min={0} max={95} step={1} onValueChange={([v]) => updateActiveField(activeField, 'y', v)} />
                </div>

                {/* Font size */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs flex items-center gap-1"><Type className="w-3 h-3" /> {t('coverEditor.fontSize')}</Label>
                    <span className="text-xs text-muted-foreground font-mono">{af.fontSize ?? 12}px</span>
                  </div>
                  <Slider value={[af.fontSize ?? 12]} min={4} max={48} step={1} onValueChange={([v]) => updateActiveField(activeField, 'fontSize', v)} />
                </div>

                {/* Color */}
                <div className="space-y-2">
                  <Label className="text-xs">{t('coverEditor.color')}</Label>
                  <div className="flex items-center gap-2">
                    <input type="color" value={af.color?.startsWith('#') ? af.color.slice(0, 7) : '#ffffff'} onChange={(e) => updateActiveField(activeField, 'color', e.target.value)} className="w-8 h-8 rounded border cursor-pointer" />
                    <Input value={af.color || '#ffffff'} onChange={(e) => updateActiveField(activeField, 'color', e.target.value)} className="h-8 text-xs font-mono flex-1" />
                  </div>
                </div>

                {/* Font family */}
                <div className="space-y-2">
                  <Label className="text-xs">{t('coverEditor.typography')}</Label>
                  <Select value={af.fontFamily || 'sans-serif'} onValueChange={(v) => updateActiveField(activeField, 'fontFamily', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value} className="text-xs">{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Font weight */}
                <div className="space-y-2">
                  <Label className="text-xs">{t('coverEditor.weight')}</Label>
                  <Select value={af.fontWeight || '400'} onValueChange={(v) => updateActiveField(activeField, 'fontWeight', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="300" className="text-xs">Light (300)</SelectItem>
                      <SelectItem value="400" className="text-xs">Normal (400)</SelectItem>
                      <SelectItem value="600" className="text-xs">Semibold (600)</SelectItem>
                      <SelectItem value="bold" className="text-xs">Bold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Text transform */}
                <div className="space-y-2">
                  <Label className="text-xs">{t('coverEditor.transform')}</Label>
                  <Select value={af.textTransform || 'none'} onValueChange={(v) => updateActiveField(activeField, 'textTransform', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" className="text-xs">{t('common.normal')}</SelectItem>
                      <SelectItem value="uppercase" className="text-xs">{t('coverEditor.uppercase')}</SelectItem>
                      <SelectItem value="capitalize" className="text-xs">{t('coverEditor.capitalize')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Text align */}
                <div className="space-y-2">
                  <Label className="text-xs">{t('coverEditor.alignment')}</Label>
                  <Select value={af.textAlign || 'left'} onValueChange={(v) => updateActiveField(activeField, 'textAlign', v)}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left" className="text-xs">{t('coverEditor.alignLeft')}</SelectItem>
                      <SelectItem value="center" className="text-xs">{t('coverEditor.alignCenter')}</SelectItem>
                      <SelectItem value="right" className="text-xs">{t('coverEditor.alignRight')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Back cover extra options */}
                {activeTab === 'back' && (
                  <div className="space-y-3 pt-3 border-t">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Resenas</p>
                    <div className="space-y-2">
                      <Label className="text-xs">Color texto resena</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={backConfig.review_color || '#333333'} onChange={(e) => setBackConfig(prev => ({ ...prev, review_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
                        <Input value={backConfig.review_color || '#333333'} onChange={(e) => setBackConfig(prev => ({ ...prev, review_color: e.target.value }))} className="h-8 text-xs font-mono flex-1" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Color autor resena</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={backConfig.reviewer_color || '#666666'} onChange={(e) => setBackConfig(prev => ({ ...prev, reviewer_color: e.target.value }))} className="w-8 h-8 rounded border cursor-pointer" />
                        <Input value={backConfig.reviewer_color || '#666666'} onChange={(e) => setBackConfig(prev => ({ ...prev, reviewer_color: e.target.value }))} className="h-8 text-xs font-mono flex-1" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Texto de marca</Label>
                      <Input value={backConfig.branding_text || ''} onChange={(e) => setBackConfig(prev => ({ ...prev, branding_text: e.target.value }))} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs">Web</Label>
                      <Input value={backConfig.website_text || ''} onChange={(e) => setBackConfig(prev => ({ ...prev, website_text: e.target.value }))} className="h-8 text-xs" />
                    </div>
                    <div>
                      <Label className="text-xs">Numero de resenas</Label>
                      <Select value={String(backConfig.num_reviews || 4)} onValueChange={(v) => setBackConfig(prev => ({ ...prev, num_reviews: Number(v) }))}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)} className="text-xs">{n} resenas</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="checkbox" checked={backConfig.show_badge !== false} onChange={(e) => setBackConfig(prev => ({ ...prev, show_badge: e.target.checked }))} className="w-4 h-4 rounded" />
                      <Label className="text-xs">Badge "Best Seller"</Label>
                    </div>
                  </div>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
